import React, { useState, useEffect } from "react";
import { UserLogin, UserData, validate2fa, validatePhoneLoginOtp, resendPhoneLoginOtp } from "../../api/index";
import Button from "react-bootstrap/Button";
import { Form } from "react-bootstrap";
import { Link, withRouter } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faEye,
    faEyeSlash,
    faArrowRight,
    faExternalLinkAlt,
    faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import KurvFooter from "./KurvFooter";
import "./KurvLogin.scss";

const KurvLogin = ({ refreshLogin, history }) => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [message, setMessage] = useState("");
    const [formError, setFormError] = useState([]);
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [requires2fa, setRequires2fa] = useState(false);
    const [twoFactorToken, setTwoFactorToken] = useState(null);
    const [twoFactorCode, setTwoFactorCode] = useState("");
    const [requiresPhoneOtp, setRequiresPhoneOtp] = useState(false);
    const [phoneOtpToken, setPhoneOtpToken] = useState(null);
    const [phoneMasked, setPhoneMasked] = useState("");
    const [resendingPhoneOtp, setResendingPhoneOtp] = useState(false);

    useEffect(() => {
        UserData()
            .then((response) => {
                if (response.id) {
                    history.push("/applications");
                }
            })
            .catch((err) => {});
    }, [history]);

    const validateForm = () => {
        return email.length > 0 && password.length > 0;
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const payload = {
            email: email,
            password: password,
        };
        UserLogin(payload)
            .then((response) => {
                if (response.requires_2fa) {
                    setRequires2fa(true);
                    setTwoFactorToken(response.two_factor_token);
                    setMessage("");
                    return;
                }
                // Verified cell number: a code has just been texted, and the
                // session is not created until it comes back correct.
                if (response.requires_phone_otp) {
                    setRequiresPhoneOtp(true);
                    setPhoneOtpToken(response.phone_otp_token);
                    setPhoneMasked(response.phone_masked || "");
                    setTwoFactorCode("");
                    setMessage("");
                    return;
                }
                refreshLogin();
                history.push("/applications");
            })
            .catch((error) => {
                setFormError(error.data.errors);
                const errorMessage = error.data.message || "";
                const lowerErrorMessage = errorMessage.toLowerCase();
                if (lowerErrorMessage.includes("these credentials do not match our records")) {
                    setMessage("Invalid email or password");
                } else {
                    setMessage(errorMessage);
                }
            });
    };

    // Shared by both challenges — the SMS code and the authenticator code are
    // entered in the same box, only the endpoint differs.
    const handle2faSubmit = (event) => {
        event.preventDefault();
        (requiresPhoneOtp
            ? validatePhoneLoginOtp({ phone_otp_token: phoneOtpToken, code: twoFactorCode })
            : validate2fa({ two_factor_token: twoFactorToken, code: twoFactorCode })
        )
            .then((response) => {
                if (response.success) {
                    refreshLogin();
                    history.push("/applications");
                } else {
                    setMessage(response.message);
                }
            })
            .catch((error) => {
                setMessage(error?.data?.message || "Invalid verification code");
            });
    };

    // Only the SMS challenge has this — an authenticator app makes its own codes.
    // Without it a burnt or expired code strands the user on this screen, since the
    // record is deleted after five wrong tries and there is nothing left to enter.
    const handleResendPhoneOtp = (event) => {
        event.preventDefault();
        if (resendingPhoneOtp) {
            return;
        }
        setResendingPhoneOtp(true);
        setMessage("");
        resendPhoneLoginOtp({ phone_otp_token: phoneOtpToken })
            .then((response) => {
                // The token is reissued with the code, so the pending login does not
                // expire before the code it is waiting for.
                if (response.phone_otp_token) {
                    setPhoneOtpToken(response.phone_otp_token);
                }
                if (response.phone_masked) {
                    setPhoneMasked(response.phone_masked);
                }
                setTwoFactorCode("");
                setMessage(response.message || "We sent a new code.");
            })
            .catch((error) => {
                if (error?.data?.restart) {
                    setRequiresPhoneOtp(false);
                    setTwoFactorCode("");
                }
                setMessage(error?.data?.message || "We could not send a new code.");
            })
            .finally(() => setResendingPhoneOtp(false));
    };

    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        setMessage("");
    };

    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
        setMessage("");
    };

    const togglePasswordVisibility = () => {
        setPasswordVisible(!passwordVisible);
    };

    return (
        <div className="kurv-login-page">
            <div className="hero-image-background" />
            <div className="kurv-login-container">
                <div className="kurv-logo">
                    <img
                        src="/images/kurv-agent/kurv-logo.svg"
                        alt="Kurv Agent"
                        className="logo-image"
                    />
                </div>

                <div className="kurv-login-card">
                    {requires2fa || requiresPhoneOtp ? (
                        <>
                            <h2 className="sign-in-title">{requiresPhoneOtp ? "Verify It's You" : "Two-Factor Authentication"}</h2>
                            <Form onSubmit={handle2faSubmit} className="login-form">
                                <p style={{ textAlign: "center", marginBottom: "20px", fontSize: "14px", color: "#666" }}>
                                    {requiresPhoneOtp
                                        ? `Enter the 6-digit code we texted to ${phoneMasked || "your phone"}`
                                        : "Enter the 6-digit code from your authenticator app or a recovery code"}
                                </p>
                                <div className="form-group">
                                    <label className="form-label">Verification Code</label>
                                    <Form.Control
                                        type="text"
                                        value={twoFactorCode}
                                        onChange={(e) => { setTwoFactorCode(e.target.value); setMessage(""); }}
                                        className="form-input"
                                        placeholder="Enter code"
                                        maxLength={requiresPhoneOtp ? 6 : 16}
                                        autoFocus
                                    />
                                </div>
                                {message && (
                                    <div className="error-message">
                                        <FontAwesomeIcon icon={faTimesCircle} className="error-icon" />
                                        <span>{message}</span>
                                    </div>
                                )}
                                <div className="button-and-join-container">
                                    <Button type="submit" className="sign-in-button" disabled={!twoFactorCode}>
                                        Verify <FontAwesomeIcon icon={faArrowRight} className="button-arrow" />
                                    </Button>
                                    <div className="join-link">
                                        {requiresPhoneOtp && (
                                            <a href="#" onClick={handleResendPhoneOtp} style={{ marginRight: "16px" }}>
                                                {resendingPhoneOtp ? "Sending..." : "Resend code"}
                                            </a>
                                        )}
                                        <a href="#" onClick={(e) => { e.preventDefault(); setRequires2fa(false); setRequiresPhoneOtp(false); setTwoFactorCode(""); setMessage(""); }}>
                                            Back to Sign In
                                        </a>
                                    </div>
                                </div>
                            </Form>
                        </>
                    ) : (
                    <>
                    <h2 className="sign-in-title">Sign In</h2>

                    <Form onSubmit={handleSubmit} className="login-form">
                        <div className="form-group">
                            <label className="form-label">Email</label>
                            <Form.Control
                                type="email"
                                value={email}
                                onChange={handleEmailChange}
                                className="form-input"
                                placeholder="Email"
                                isInvalid={formError && formError.email}
                            />
                            <Form.Control.Feedback
                                type="invalid"
                                className="text-capitalize"
                            >
                                {formError && formError.email
                                    ? formError.email
                                    : null}
                            </Form.Control.Feedback>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Password</label>
                            <div className="password-input-container">
                                <Form.Control
                                    type={passwordVisible ? "text" : "password"}
                                    value={password}
                                    onChange={handlePasswordChange}
                                    className="form-input"
                                    placeholder="Enter password"
                                    isInvalid={formError && formError.password}
                                />
                                <FontAwesomeIcon
                                    icon={passwordVisible ? faEyeSlash : faEye}
                                    className="password-toggle"
                                    onClick={togglePasswordVisibility}
                                />
                            </div>
                            <Form.Control.Feedback
                                type="invalid"
                                className="text-capitalize"
                            >
                                {formError && formError.password
                                    ? formError.password
                                    : null}
                            </Form.Control.Feedback>
                        </div>

                        <div className="forgot-password-link">
                            <Link to="/forgotPassword">Forget Password?</Link>
                        </div>

                        {message && (
                            <div className="error-message">
                                <FontAwesomeIcon
                                    icon={faTimesCircle}
                                    className="error-icon"
                                />
                                <span>{message}</span>
                            </div>
                        )}

                        <div className="button-and-join-container">
                            <Button
                                type="submit"
                                className="sign-in-button"
                                disabled={!validateForm()}
                            >
                                Sign In
                                <FontAwesomeIcon
                                    icon={faArrowRight}
                                    className="button-arrow"
                                />
                            </Button>

                            <div className="join-link">
                                <span>Not an agent yet? </span>
                                <a
                                    href="https://kurvpay.com/partners/agent-iso/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="learn-more-link"
                                >
                                    Learn how to join
                                </a>
                            </div>
                        </div>
                    </Form>
                    </>
                    )}
                </div>

                <a
                    href="https://myportfolio.emscorporate.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="kurv-stratus-logo"
                >
                    <img
                        src="/images/kurv-agent/myportfolio-logo.png"
                        alt="My Portfolio"
                    />
                </a>
            </div>

            <KurvFooter />
        </div>
    );
};

export default withRouter(KurvLogin);
